import type { ActivitySource } from "@/shared/api/activity-source";
import { ApiError, jsonFromResponse, normalizeApiError, withQuery } from "@/shared/api/client";
import { authFetch, authPost, isSignedIn, captureAuthSessionOwner, isAuthSessionOwnerCurrent } from "@/shared/auth/session";
import { getWebDeviceId } from "@/shared/auth/web-device-id.mjs";
import {
  captureGoogleIntegrationsRuntimeGeneration,
  GoogleIntegrationsRuntimeSupersededError,
  isGoogleIntegrationsRuntimeGenerationCurrent,
} from "@/shared/google/google-integration-runtime.mjs";

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const OPENID_SCOPE = "openid";
const GOOGLE_USERINFO_EMAIL_SCOPE = "https://www.googleapis.com/auth/userinfo.email";
const GOOGLE_USERINFO_PROFILE_SCOPE = "https://www.googleapis.com/auth/userinfo.profile";
const CALENDAR_EVENTS_OWNED_SCOPE = "https://www.googleapis.com/auth/calendar.events.owned";
const GOOGLE_TASKS_SCOPE = "https://www.googleapis.com/auth/tasks";
const GOOGLE_REDIRECT_LAUNCH_TIMEOUT_MS = 10_000;
const GOOGLE_IDENTITY_SCOPES = [
  OPENID_SCOPE,
  GOOGLE_USERINFO_EMAIL_SCOPE,
  GOOGLE_USERINFO_PROFILE_SCOPE,
];
const GOOGLE_SUPPORTED_CONNECTION_SCOPES = new Set([
  ...GOOGLE_IDENTITY_SCOPES,
  CALENDAR_EVENTS_OWNED_SCOPE,
  GOOGLE_TASKS_SCOPE,
]);

export type GoogleCalendarAuthorizationIntent =
  | "initial-full-access"
  | "calendar-write"
  | "tasks-write";

let gisScriptPromise: Promise<void> | null = null;

function assertGoogleIntegrationsRuntimeCurrent(
  generation: number | null,
): asserts generation is number {
  if (!isGoogleIntegrationsRuntimeGenerationCurrent(generation)) {
    throw new GoogleIntegrationsRuntimeSupersededError();
  }
}

export type CalendarCapabilities = {
  calendarEventsWritable: boolean;
  tasksWritable: boolean;
};

export type CalendarStatus = {
  ok?: boolean;
  connected: boolean;
  email?: string | null;
  displayName?: string | null;
  connectedAt?: string | null;
  accountId?: string | null;
  connectionVersion?: number | null;
  scopes: string[];
  capabilities: CalendarCapabilities;
  limited: boolean;
  reauthorizationRequired?: boolean;
};

type CalendarStatusPayload = Partial<Omit<CalendarStatus, "capabilities" | "scopes">> & {
  scopes?: string[] | string | null;
  capabilities?: (Partial<CalendarCapabilities> & { limited?: boolean }) | null;
};

export type CalendarAttendee = {
  email: string;
  displayName?: string | null;
  responseStatus?: string | null;
  optional?: boolean;
  organizer?: boolean;
  self?: boolean;
};

export type CalendarEvent = {
  id: string;
  calendarId: string;
  etag?: string | null;
  calendarSummary?: string | null;
  title: string;
  status: string;
  allDay: boolean;
  startEpochMs: number | null;
  endEpochMs: number | null;
  timeZone?: string | null;
  location?: string | null;
  hangoutLink?: string | null;
  conferenceLink?: string | null;
  htmlLink?: string | null;
  description?: string | null;
  recurringEventId?: string | null;
  recurrence?: string[];
  attendees?: CalendarAttendee[];
};

export type GoogleTaskCanonicalState =
  | "provider_owned"
  | "active"
  | "pending_adoption"
  | "tombstoned"
  | "unknown";

export type GoogleTask = {
  id: string;
  googleTaskId: string;
  taskListId: string;
  etag?: string | null;
  taskListTitle: string;
  title: string;
  notes?: string | null;
  status: string;
  dueEpochMs?: number | null;
  completedEpochMs?: number | null;
  updatedEpochMs?: number | null;
  linkedClientId?: string | null;
  fromTodaysBrief?: boolean;
  canonicalClientId?: string | null;
  canonicalState?: GoogleTaskCanonicalState | null;
};

export type CalendarAgenda = {
  ok?: boolean;
  timeZone?: string | null;
  timeMin?: string;
  timeMax?: string;
  calendarIds?: string[];
  taskLists?: string[];
  calendarSyncError?: string | null;
  tasksSyncError?: string | null;
  syncedTaskListIds?: string[];
  tasksSnapshotComplete?: boolean;
  events: CalendarEvent[];
  tasks: GoogleTask[];
  syncedAtEpochMs?: number | null;
  writebackConflicts?: number;
};

export type CalendarEventDraft = {
  calendarId?: string;
  eventId?: string;
  etag?: string;
  title: string;
  startEpochMs: number;
  endEpochMs: number;
  allDay?: boolean;
  timeZone?: string;
  location?: string;
  description?: string;
  reminderMinutes?: number | null;
};

type CalendarEventMutationResponse = {
  ok?: boolean;
  event?: CalendarEvent;
  calendarId?: string;
  eventId?: string;
  deleted?: boolean;
  syncedAtEpochMs?: number | null;
};

export type GoogleTaskUpdateInput = {
  taskListId: string;
  googleTaskId: string;
  etag?: string;
  title: string;
  notes?: string | null;
  dueEpochMs?: number | null;
  completedEpochMs?: number | null;
  timeZone?: string;
};

export type GoogleTaskDeleteInput = {
  taskListId: string;
  googleTaskId: string;
  etag?: string;
};

export type GoogleTaskMutationResponse = {
  ok?: boolean;
  task?: GoogleTask;
  taskListId?: string;
  taskId?: string;
  deleted?: boolean;
  syncedAtEpochMs?: number | null;
};

type GoogleOAuthWindow = Window & {
  google?: {
    accounts?: {
      oauth2?: {
        initCodeClient?: (config: {
          client_id: string;
          scope: string;
          ux_mode: "redirect";
          select_account?: boolean;
          include_granted_scopes?: boolean;
          login_hint?: string;
          redirect_uri?: string;
          state?: string;
        }) => { requestCode: () => void };
      };
    };
  };
};

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function calendarCapabilities(
  status:
    | {
        scopes?: string[] | string | null;
        capabilities?: Partial<CalendarCapabilities> | null;
        reauthorizationRequired?: boolean;
      }
    | null
    | undefined,
): CalendarCapabilities {
  if (status?.reauthorizationRequired === true) {
    return {
      calendarEventsWritable: false,
      tasksWritable: false,
    };
  }
  const scopes = new Set(normalizeGoogleScopes(status?.scopes));
  const unsupportedScopePresent = [...scopes].some(
    (scope) => !GOOGLE_SUPPORTED_CONNECTION_SCOPES.has(scope),
  );
  if (unsupportedScopePresent) {
    // A retained unsupported Google authorization may return an
    // exact CRUD scope alongside a broader/read-only/unknown product scope.
    // Treat that mixed grant as nonfunctional even if an older server omitted
    // reauthorizationRequired; the user must disconnect before fresh consent.
    return {
      calendarEventsWritable: false,
      tasksWritable: false,
    };
  }
  const calendarEventsWritable = scopes.has(CALENDAR_EVENTS_OWNED_SCOPE);
  const tasksWritable = scopes.has(GOOGLE_TASKS_SCOPE);
  return {
    // Provider-confirmed exact scopes are the only client authority. Reported
    // capability booleans remain an inert response shape and cannot activate
    // access when scopes are absent, stale, read-only, broad, or unknown.
    calendarEventsWritable,
    tasksWritable,
  };
}

export function hasCalendarWriteScope(status: CalendarStatus | null | undefined): boolean {
  return calendarCapabilities(status).calendarEventsWritable;
}

export function hasGoogleTasksWriteScope(status: CalendarStatus | null | undefined): boolean {
  return calendarCapabilities(status).tasksWritable;
}

export function googleCalendarScopes(
  intent: GoogleCalendarAuthorizationIntent = "initial-full-access",
): string {
  let featureScopes: string[];
  switch (intent) {
    case "initial-full-access":
      featureScopes = [CALENDAR_EVENTS_OWNED_SCOPE, GOOGLE_TASKS_SCOPE];
      break;
    case "calendar-write":
      featureScopes = [CALENDAR_EVENTS_OWNED_SCOPE];
      break;
    case "tasks-write":
      featureScopes = [GOOGLE_TASKS_SCOPE];
      break;
    default:
      throw new ApiError({
        status: 400,
        code: "google_authorization_intent_invalid",
        detail: "A recognized Google Calendar authorization intent is required.",
      });
  }
  return [...GOOGLE_IDENTITY_SCOPES, ...featureScopes].join(" ");
}

export async function fetchCalendarStatus(
  opts: { signal?: AbortSignal; activitySource?: ActivitySource } = {},
): Promise<CalendarStatus> {
  const owner = isSignedIn() ? captureAuthSessionOwner() : null;
  if (!owner) return normalizeCalendarStatus({ ok: true, connected: false });
  const res = await authFetch("/api/calendar/status", {
    signal: opts.signal,
    activitySource: opts.activitySource,
  });
  const data = await jsonFromResponse<CalendarStatusPayload>(res, {
    fallbackCode: "calendar_status_failed",
  });
  if (!isSignedIn() || !isAuthSessionOwnerCurrent(owner)) {
    throw new ApiError({ status: 409, code: "auth_session_mutation_superseded" });
  }
  return normalizeCalendarStatus(data);
}

export async function fetchCalendarAgenda(
  opts: {
    fromMs?: number;
    activitySource?: ActivitySource;
    signal?: AbortSignal;
    toMs?: number;
    timeZone?: string;
  } = {},
): Promise<CalendarAgenda> {
  const res = await authFetch(
    withQuery("/api/calendar/agenda", {
      fromMs: opts.fromMs,
      toMs: opts.toMs,
      tz: opts.timeZone || browserTimeZone(),
    }),
    { signal: opts.signal, activitySource: opts.activitySource },
  );
  const data = await jsonFromResponse<Partial<CalendarAgenda>>(res, {
    fallbackCode: "calendar_agenda_failed",
  });
  return {
    ok: data.ok,
    timeZone: data.timeZone || opts.timeZone || browserTimeZone(),
    timeMin: data.timeMin,
    timeMax: data.timeMax,
    calendarIds: Array.isArray(data.calendarIds) ? data.calendarIds.filter(isString) : [],
    taskLists: Array.isArray(data.taskLists) ? data.taskLists.filter(isString) : [],
    calendarSyncError: data.calendarSyncError || null,
    tasksSyncError: data.tasksSyncError || null,
    syncedTaskListIds: Array.isArray(data.syncedTaskListIds)
      ? data.syncedTaskListIds.filter(isString)
      : undefined,
    tasksSnapshotComplete:
      typeof data.tasksSnapshotComplete === "boolean" ? data.tasksSnapshotComplete : undefined,
    events: Array.isArray(data.events)
      ? data.events.map(normalizeEvent).filter(isCalendarEvent)
      : [],
    tasks: Array.isArray(data.tasks) ? data.tasks.map(normalizeTask).filter(isGoogleTask) : [],
    syncedAtEpochMs: finiteOrNull(data.syncedAtEpochMs),
  };
}

type GoogleCalendarOAuthStartPayload = {
  ok?: boolean;
  attemptId?: unknown;
  state?: unknown;
  redirectUri?: unknown;
  authorizationIntent?: unknown;
  scopes?: unknown;
};

export type GoogleCalendarOAuthStart = {
  attemptId: string;
  state: string;
  redirectUri: string;
  authorizationIntent: GoogleCalendarAuthorizationIntent;
  scopes: string[];
};

export type GoogleCalendarOAuthCompletion = {
  connection: CalendarStatus;
  authorizationIntent: GoogleCalendarAuthorizationIntent;
};

function invalidGoogleCalendarOAuthResponse(detail: string): ApiError {
  return new ApiError({
    status: 502,
    code: "google_oauth_invalid_response",
    detail,
  });
}

function normalizeGoogleCalendarOAuthOpaque(value: unknown): string {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized || normalized.length > 1_024 || /[\u0000-\u001f\u007f]/.test(normalized)) {
    return "";
  }
  return normalized;
}

function normalizeGoogleCalendarAuthorizationIntent(
  value: unknown,
): GoogleCalendarAuthorizationIntent | null {
  if (value === "initial-full-access" || value === "calendar-write" || value === "tasks-write") {
    return value;
  }
  return null;
}

function sameGoogleScopes(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  if (new Set(left).size !== left.length || new Set(right).size !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((scope) => rightSet.has(scope));
}

function normalizeGoogleCalendarOAuthRedirectUri(value: unknown): string {
  const redirectUri = typeof value === "string" ? value.trim() : "";
  if (!redirectUri || typeof window === "undefined") return "";
  try {
    const parsed = new URL(redirectUri);
    if (
      parsed.origin !== window.location.origin ||
      (parsed.protocol !== "https:" &&
        parsed.hostname !== "localhost" &&
        parsed.hostname !== "127.0.0.1") ||
      parsed.pathname !== "/api/calendar/oauth/callback" ||
      parsed.username ||
      parsed.password ||
      parsed.search ||
      parsed.hash
    ) {
      return "";
    }
    return parsed.toString();
  } catch {
    return "";
  }
}

export async function beginGoogleCalendarOAuthRedirect(
  intent: GoogleCalendarAuthorizationIntent,
): Promise<GoogleCalendarOAuthStart> {
  const data = await authPost<GoogleCalendarOAuthStartPayload>(
    "/api/calendar/oauth/start",
    {
      intent,
    },
    { activitySource: "user_initiated" },
  );
  const attemptId = normalizeGoogleCalendarOAuthOpaque(data.attemptId);
  const state = normalizeGoogleCalendarOAuthOpaque(data.state);
  const redirectUri = normalizeGoogleCalendarOAuthRedirectUri(data.redirectUri);
  const authorizationIntent = normalizeGoogleCalendarAuthorizationIntent(data.authorizationIntent);
  const scopes = normalizeGoogleScopes(data.scopes);
  const expectedScopes = normalizeGoogleScopes(googleCalendarScopes(intent));
  if (
    !attemptId ||
    !state ||
    !redirectUri ||
    data.ok !== true ||
    authorizationIntent !== intent ||
    !sameGoogleScopes(scopes, expectedScopes)
  ) {
    throw invalidGoogleCalendarOAuthResponse(
      "Google Calendar authorization returned an invalid start response.",
    );
  }
  return { attemptId, state, redirectUri, authorizationIntent, scopes };
}

export async function completeGoogleCalendarOAuthRedirect(
  attemptId: string,
  options: { signal?: AbortSignal } = {},
): Promise<GoogleCalendarOAuthCompletion> {
  const normalizedAttemptId = String(attemptId || "").trim();
  if (!normalizedAttemptId) {
    throw new ApiError({
      status: 400,
      code: "google_oauth_attempt_required",
      detail: "Google Calendar authorization attempt is missing.",
    });
  }
  const response = await authFetch("/api/calendar/oauth/complete", {
    activitySource: "unknown",
    method: "POST",
    body: JSON.stringify({ attemptId: normalizedAttemptId, deviceId: getWebDeviceId() }),
    signal: options.signal,
  });
  const data = await jsonFromResponse<CalendarStatusPayload & { authorizationIntent?: unknown }>(
    response,
    { fallbackCode: "calendar_oauth_complete_failed" },
  );
  const authorizationIntent = normalizeGoogleCalendarAuthorizationIntent(data.authorizationIntent);
  if (data.ok !== true || !authorizationIntent) {
    throw invalidGoogleCalendarOAuthResponse(
      "Google Calendar authorization returned an invalid completion response.",
    );
  }
  const connection = normalizeCalendarStatus(data);
  if (
    !connection.connected ||
    (!connection.capabilities.calendarEventsWritable && !connection.capabilities.tasksWritable)
  ) {
    throw invalidGoogleCalendarOAuthResponse(
      "Google Calendar authorization returned an unusable completion response.",
    );
  }
  return {
    connection,
    authorizationIntent,
  };
}

export async function redirectGoogleCalendarAuthorization(opts: {
  clientId: string;
  includeGrantedScopes?: boolean;
  intent: GoogleCalendarAuthorizationIntent;
  launchTimeoutMs?: number;
  loginHint?: string | null;
  runtimeGeneration?: number | null;
  selectAccount?: boolean;
}): Promise<never> {
  const runtimeGeneration =
    opts.runtimeGeneration === undefined
      ? captureGoogleIntegrationsRuntimeGeneration()
      : opts.runtimeGeneration;
  assertGoogleIntegrationsRuntimeCurrent(runtimeGeneration);
  const clientId = opts.clientId.trim();
  if (!clientId) {
    throw new ApiError({
      status: 400,
      code: "google_client_id_missing",
      detail: "Google Calendar connect is not configured for this web app.",
    });
  }
  // Do not allocate a server attempt until the browser can launch GIS. A
  // script-blocked browser can retry without leaving an unused attempt behind.
  await loadGoogleIdentityScript();
  assertGoogleIntegrationsRuntimeCurrent(runtimeGeneration);
  const oauth = (window as GoogleOAuthWindow).google?.accounts?.oauth2;
  if (!oauth?.initCodeClient) {
    throw new ApiError({
      status: 0,
      code: "google_identity_unavailable",
      detail: "Google Calendar connect did not load. Check your connection and try again.",
    });
  }
  const authorization = await beginGoogleCalendarOAuthRedirect(opts.intent);
  assertGoogleIntegrationsRuntimeCurrent(runtimeGeneration);
  const codeClient = oauth.initCodeClient({
    client_id: clientId,
    scope: googleCalendarScopes(opts.intent),
    ux_mode: "redirect",
    redirect_uri: authorization.redirectUri,
    state: authorization.state,
    include_granted_scopes: opts.includeGrantedScopes !== false,
    select_account: opts.selectAccount !== false,
    login_hint: opts.loginHint || undefined,
  });
  if (!codeClient || typeof codeClient.requestCode !== "function") {
    throw new ApiError({
      status: 0,
      code: "google_identity_unavailable",
      detail: "Google Calendar connect did not initialize. Try again.",
    });
  }
  assertGoogleIntegrationsRuntimeCurrent(runtimeGeneration);
  // GIS commits a top-level navigation asynchronously. Keep React Query's
  // mutation pending until document unload so a second tap cannot rotate the
  // server attempt and invalidate the state already handed to Google.
  return new Promise<never>((_resolve, reject) => {
    let leftDocument = false;
    let settled = false;
    let launchTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
    const startedAt = Date.now();
    const cleanup = () => {
      if (launchTimer !== null) globalThis.clearTimeout(launchTimer);
      launchTimer = null;
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
    const rejectOnce = (error: ApiError) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const onPageHide = () => {
      leftDocument = true;
      if (launchTimer !== null) globalThis.clearTimeout(launchTimer);
      launchTimer = null;
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted && !leftDocument) return;
      rejectOnce(
        new ApiError({
          status: 409,
          code: "google_authorization_interrupted",
          detail: "Google authorization returned before the review was completed.",
        }),
      );
    };
    const checkLaunchDeadline = () => {
      if (settled || leftDocument) return;
      const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
      const timeoutMs = Math.max(1, opts.launchTimeoutMs || GOOGLE_REDIRECT_LAUNCH_TIMEOUT_MS);
      if (visible && Date.now() - startedAt >= timeoutMs) {
        rejectOnce(
          new ApiError({
            status: 0,
            code: "google_authorization_launch_failed",
            detail: "Google authorization did not open. Try connecting again.",
          }),
        );
        return;
      }
      const remainingMs = Math.max(1, timeoutMs - (Date.now() - startedAt));
      launchTimer = globalThis.setTimeout(
        checkLaunchDeadline,
        visible ? Math.min(1_000, remainingMs) : 1_000,
      );
    };
    // Chrome and Android can restore the pre-consent document from BFCache
    // after a Back gesture. Release the never-settling launch mutation in that
    // restored document so Connect is usable again without a manual reload.
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    launchTimer = globalThis.setTimeout(
      checkLaunchDeadline,
      Math.min(1_000, Math.max(1, opts.launchTimeoutMs || GOOGLE_REDIRECT_LAUNCH_TIMEOUT_MS)),
    );
    try {
      assertGoogleIntegrationsRuntimeCurrent(runtimeGeneration);
      codeClient.requestCode();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

export async function disconnectGoogleCalendar(): Promise<{ ok?: boolean; connected?: boolean }> {
  return authPost("/api/calendar/disconnect", {}, { activitySource: "user_initiated" });
}

export async function createCalendarEvent(
  draft: CalendarEventDraft,
): Promise<CalendarEventMutationResponse> {
  const res = await authFetch("/api/calendar/events", {
    activitySource: "user_initiated",
    method: "POST",
    body: JSON.stringify(draft),
  });
  return jsonFromResponse<CalendarEventMutationResponse>(res, {
    fallbackCode: "calendar_event_create_failed",
  });
}

export async function updateCalendarEvent(
  draft: CalendarEventDraft,
): Promise<CalendarEventMutationResponse> {
  const res = await authFetch("/api/calendar/events", {
    activitySource: "user_initiated",
    method: "PATCH",
    body: JSON.stringify(draft),
  });
  return jsonFromResponse<CalendarEventMutationResponse>(res, {
    fallbackCode: "calendar_event_update_failed",
  });
}

export async function deleteCalendarEvent(input: {
  calendarId: string;
  eventId: string;
  etag?: string;
}): Promise<CalendarEventMutationResponse> {
  const res = await authFetch("/api/calendar/events", {
    activitySource: "user_initiated",
    method: "DELETE",
    body: JSON.stringify(input),
  });
  return jsonFromResponse<CalendarEventMutationResponse>(res, {
    fallbackCode: "calendar_event_delete_failed",
  });
}

export function googleTaskUpdateInput(
  task: GoogleTask,
  changes: Partial<
    Pick<GoogleTaskUpdateInput, "title" | "notes" | "dueEpochMs" | "completedEpochMs">
  > = {},
  timeZone = browserTimeZone(),
): GoogleTaskUpdateInput {
  return {
    taskListId: task.taskListId,
    googleTaskId: task.googleTaskId,
    etag: task.etag || undefined,
    title: changes.title ?? task.title,
    notes: changes.notes !== undefined ? changes.notes : task.notes || null,
    dueEpochMs: changes.dueEpochMs !== undefined ? changes.dueEpochMs : (task.dueEpochMs ?? null),
    // Omitting completion is materially different from sending null: null
    // reopens the Google Task. Metadata-only edits must preserve provider
    // status even when an older/partial row omitted its completion timestamp.
    ...(changes.completedEpochMs !== undefined
      ? { completedEpochMs: changes.completedEpochMs }
      : {}),
    timeZone,
  };
}

export async function updateGoogleTask(
  input: GoogleTaskUpdateInput,
): Promise<GoogleTaskMutationResponse> {
  const res = await authFetch("/api/calendar/google-tasks", {
    activitySource: "user_initiated",
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const data = await jsonFromResponse<GoogleTaskMutationResponse>(res, {
    fallbackCode: "google_task_update_failed",
  });
  const task = data.task ? normalizeTask(data.task) : null;
  if (!task) {
    throw new ApiError({
      status: 502,
      code: "google_task_update_invalid_response",
      detail: "Google Tasks returned an invalid update response.",
    });
  }
  return { ...data, task };
}

export async function deleteGoogleTask(
  input: GoogleTaskDeleteInput,
): Promise<GoogleTaskMutationResponse> {
  const res = await authFetch("/api/calendar/google-tasks", {
    activitySource: "user_initiated",
    method: "DELETE",
    body: JSON.stringify(input),
  });
  return jsonFromResponse<GoogleTaskMutationResponse>(res, {
    fallbackCode: "google_task_delete_failed",
  });
}

export async function syncGoogleTasks(
  timeZone = browserTimeZone(),
  opts: { signal?: AbortSignal; activitySource?: ActivitySource } = {},
): Promise<CalendarAgenda> {
  const res = await authFetch(withQuery("/api/calendar/tasks/sync", { tz: timeZone }), {
    activitySource: opts.activitySource,
    method: "POST",
    body: JSON.stringify({ timeZone }),
    signal: opts.signal,
  });
  const data = await jsonFromResponse<Partial<CalendarAgenda>>(res, {
    fallbackCode: "google_tasks_sync_failed",
  });
  return {
    ok: data.ok,
    timeZone,
    events: [],
    tasks: Array.isArray(data.tasks) ? data.tasks.map(normalizeTask).filter(isGoogleTask) : [],
    syncedTaskListIds: Array.isArray(data.syncedTaskListIds)
      ? data.syncedTaskListIds.filter(isString)
      : undefined,
    tasksSnapshotComplete:
      typeof data.tasksSnapshotComplete === "boolean" ? data.tasksSnapshotComplete : undefined,
    syncedAtEpochMs: finiteOrNull(data.syncedAtEpochMs),
    writebackConflicts:
      Number.isSafeInteger(Number(data.writebackConflicts)) && Number(data.writebackConflicts) > 0
        ? Number(data.writebackConflicts)
        : 0,
  };
}

export function calendarErrorMessage(err: unknown): string {
  const apiErr = normalizeApiError(err, "calendar_failed");
  if (apiErr.code === "calendar_disabled") return "Calendar is disabled on this server.";
  if (apiErr.code === "calendar_not_connected")
    return "Connect Google Calendar to see your agenda.";
  if (apiErr.code === "calendar_write_permission_required") {
    return "Reconnect Google Calendar with event access to make changes.";
  }
  if (apiErr.code === "google_tasks_write_permission_required") {
    return "Reconnect Google Calendar with Google Tasks access to sync tasks.";
  }
  if (apiErr.code === "google_not_configured" || apiErr.code === "google_client_id_missing") {
    return "Google Calendar connect is not configured for this server.";
  }
  if (apiErr.code === "google_scope_denied") {
    return "Google did not grant the requested access. Review the selected permissions and try again.";
  }
  if (apiErr.code === "google_authorization_cancelled") {
    return "Google connection was cancelled. No access was changed.";
  }
  if (apiErr.code === "google_product_scope_required") {
    return "This feature needs additional Google access. Enable access for that feature and try again.";
  }
  if (apiErr.code === "calendar_disconnect_required") {
    return "Disconnect the existing Google link, then connect again to choose supported Calendar Events and Tasks access.";
  }
  if (apiErr.status === 0 || apiErr.code === "network_error") {
    return apiErr.detail || "Calendar needs a connection. Try again when you are online.";
  }
  return apiErr.detail || apiErr.message || "Calendar could not complete that request.";
}

function loadGoogleIdentityScript(): Promise<void> {
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise<void>((resolve, reject) => {
    if ((window as GoogleOAuthWindow).google?.accounts?.oauth2?.initCodeClient) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("google_identity_unavailable")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("google_identity_unavailable"));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // A transient CSP/network failure must not poison every later user retry.
    gisScriptPromise = null;
    throw error;
  });
  return gisScriptPromise;
}

function normalizeCalendarStatus(data: CalendarStatusPayload): CalendarStatus {
  const connected = data.connected === true;
  const scopes = normalizeGoogleScopes(data.scopes);
  const reauthorizationRequired = data.reauthorizationRequired === true;
  const capabilities = calendarCapabilities({
    scopes,
    capabilities: data.capabilities,
    reauthorizationRequired,
  });
  const reportedConnectionVersion =
    data.connectionVersion == null ? null : Number(data.connectionVersion);
  const connectionVersion =
    typeof reportedConnectionVersion === "number" &&
    Number.isSafeInteger(reportedConnectionVersion) && reportedConnectionVersion >= 0
      ? reportedConnectionVersion
      : null;
  return {
    connected,
    email: data.email || null,
    displayName: data.displayName || null,
    connectedAt: data.connectedAt || null,
    accountId: data.accountId || null,
    connectionVersion,
    scopes,
    capabilities,
    reauthorizationRequired,
    limited: connected && (!capabilities.calendarEventsWritable || !capabilities.tasksWritable),
    ok: data.ok,
  };
}

function normalizeEvent(value: CalendarEvent): CalendarEvent | null {
  if (!value || !value.id || !value.calendarId) return null;
  const start = finiteOrNull(value.startEpochMs);
  if (start == null) return null;
  return {
    id: String(value.id),
    calendarId: String(value.calendarId),
    etag: value.etag ? String(value.etag) : null,
    calendarSummary: value.calendarSummary || null,
    title: String(value.title || "(No title)"),
    status: String(value.status || "confirmed"),
    allDay: value.allDay === true,
    startEpochMs: start,
    endEpochMs: finiteOrNull(value.endEpochMs) ?? start,
    timeZone: value.timeZone || null,
    location: value.location || null,
    hangoutLink: value.hangoutLink || null,
    conferenceLink: value.conferenceLink || null,
    htmlLink: value.htmlLink || null,
    description: value.description || null,
    recurringEventId: value.recurringEventId || null,
    recurrence: Array.isArray(value.recurrence) ? value.recurrence.filter(isString) : [],
    attendees: Array.isArray(value.attendees) ? value.attendees : [],
  };
}

function isCalendarEvent(value: CalendarEvent | null): value is CalendarEvent {
  return value != null;
}

function normalizeTask(value: GoogleTask): GoogleTask | null {
  if (!value || !value.id || !value.googleTaskId || !value.taskListId) return null;
  const reportedCanonicalState = String(value.canonicalState || "").trim();
  const canonicalState = !reportedCanonicalState
    ? null
    : ["provider_owned", "active", "pending_adoption", "tombstoned"].includes(
          reportedCanonicalState,
        )
      ? (reportedCanonicalState as GoogleTaskCanonicalState)
      : "unknown";
  return {
    id: String(value.id),
    googleTaskId: String(value.googleTaskId),
    taskListId: String(value.taskListId),
    etag: value.etag ? String(value.etag) : null,
    taskListTitle: String(value.taskListTitle || "Google Tasks"),
    title: String(value.title || "(No title)"),
    notes: value.notes || null,
    status: String(value.status || "needsAction"),
    dueEpochMs: finiteOrNull(value.dueEpochMs),
    completedEpochMs: finiteOrNull(value.completedEpochMs),
    updatedEpochMs: finiteOrNull(value.updatedEpochMs),
    linkedClientId: value.linkedClientId || null,
    fromTodaysBrief: value.fromTodaysBrief === true,
    canonicalClientId: value.canonicalClientId || null,
    canonicalState,
  };
}

function isGoogleTask(value: GoogleTask | null): value is GoogleTask {
  return value != null;
}

export function isGoogleTaskProjectionEvent(event: CalendarEvent): boolean {
  return event.calendarId.startsWith("google-tasks:");
}

export function finiteOrNull(value: unknown): number | null {
  if (value == null || (typeof value === "string" && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function normalizeGoogleScopes(value: unknown): string[] {
  const values = (Array.isArray(value) ? value : [value]).flatMap((entry) =>
    typeof entry === "string" ? entry.split(/\s+/) : [],
  );
  return [
    ...new Set(
      values
        .filter(isString)
        .map((scope) => scope.trim())
        .filter(Boolean),
    ),
  ];
}
